import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  adminLogin: (data) => api.post('/admin/login', data),
};

// Payment APIs
export const paymentAPI = {
  createOrder: (amount = 19900) => api.post('/payment/create-order', { amount }),
  verifyPayment: (data, userId) => api.post(`/payment/verify?user_id=${userId}`, data),
  completePayment: (userId) => api.post(`/payment/complete/${userId}`),
  getRazorpayKey: () => api.get('/razorpay-key'),
};

// Entry APIs
export const entryAPI = {
  getAll: () => api.get('/entries'),
  getOne: (id) => api.get(`/entries/${id}`),
  create: (data) => api.post('/entries', data),
  update: (id, data) => api.patch(`/entries/${id}`, data),
  delete: (id) => api.delete(`/entries/${id}`),
};

// LO Code APIs
export const loCodeAPI = {
  getAll: (subject, className) => {
    const params = new URLSearchParams();
    if (subject) params.append('subject', subject);
    if (className) params.append('class_name', className);
    return api.get(`/lo-codes?${params}`);
  },
  create: (data) => api.post('/lo-codes', data),
};

// Admin LO Code APIs
export const adminLOCodeAPI = {
  getAll: (subject, className) => {
    const params = new URLSearchParams();
    if (subject) params.append('subject', subject);
    if (className) params.append('class_name', className);
    return api.get(`/admin/lo-codes?${params}`);
  },
  create: (data) => api.post('/admin/lo-codes', data),
  bulkCreate: (codes) => api.post('/admin/lo-codes/bulk', codes),
  update: (id, data) => api.patch(`/admin/lo-codes/${id}`, data),
  delete: (id) => api.delete(`/admin/lo-codes/${id}`),
  bulkDelete: (className, subject) => {
    const params = new URLSearchParams();
    if (className) params.append('class_name', className);
    if (subject) params.append('subject', subject);
    return api.delete(`/admin/lo-codes/bulk/delete?${params}`);
  },
};

// Blueprint Library APIs
export const bpLibraryAPI = {
  getAll: (className, subject) => {
    const params = new URLSearchParams();
    if (className) params.append('class_name', className);
    if (subject) params.append('subject', subject);
    return api.get(`/bp-library?${params}`);
  },
  getOne: (id) => api.get(`/bp-library/${id}`),
  create: (data) => api.post('/bp-library', data),
  update: (id, data) => api.patch(`/bp-library/${id}`, data),
  delete: (id) => api.delete(`/bp-library/${id}`),
};

// Admin APIs
export const adminAPI = {
  getUsers: (search) => api.get(`/admin/users${search ? `?search=${search}` : ''}`),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getPayments: () => api.get('/admin/payments'),
  getStats: () => api.get('/admin/stats'),
  getEntries: () => api.get('/admin/entries'),
  deleteEntry: (id) => api.delete(`/admin/entries/${id}`),
};

export default api;
